/* eslint-disable react/no-array-index-key */
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import { FieldArray, FormikProps, useFormikContext } from 'formik';
import React, { ReactNode, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import {
  CoachingDetailsInput,
  CoachingFormInputData,
  defaultCoachingDetailsValues,
  StatusOptionsObject,
} from '../../../api/CoachingForm/schema';
import { getCSCEndpoint } from '../../../config/Config';
import { renderUrl } from '../../../config/Url';
import { ContactFromGetter } from '../ContactReview/Data';
import ConditionalTooltip from '../FieldComponents/ConditionalTooltip';
import ErrorMessage from '../FieldComponents/ErrorMessage';
import { getLabelFromValue } from '../FieldComponents/Helpers';
import FieldArrayDisplay, {
  DisplayObject,
} from '../LayoutComponents/FieldArrayDisplay';
import {
  getAllBehaviors,
  getAllMetrics,
  getAllTenets,
  getAllTopics,
} from './Data';
import Subform from './Subform';

export const convertDetailsToDisplay = (
  detail: CoachingDetailsInput,
  intl: IntlShape,
  handleReplace?: (value: CoachingDetailsInput) => void,
  isTeam?: boolean,
  contactList?: ContactFromGetter[]
): DisplayObject[] => {
  const {
    coachingTopic,
    relatedContacts,
    relatedContactDomains,
    tenets,
    behaviors,
    relatedMetric,
    assignedAction,
    status,
  } = detail;

  const display: DisplayObject[] = [
    // Topic
    {
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_readonly_coaching_topic',
        defaultMessage: 'Coaching topic:',
      }),
      value: getLabelFromValue(coachingTopic, getAllTopics(intl)),
    },
  ];

  // Related Contacts
  if (relatedContactDomains && relatedContactDomains.length) {
    display.push({
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_readonly_related_contacts',
        defaultMessage: 'Related contacts:',
      }),
      value: relatedContactDomains.map(domainInfo => {
        return domainInfo.marketplaceDomainName !== null
          ? {
              text: domainInfo.cscContactID,
              href: renderUrl(
                // eslint-disable-next-line max-len
                `/gp/stores/${domainInfo.marketplaceDomainName}/gp/communications/issue-manager?initialCommId=${domainInfo.cscContactID}`,
                {
                  externalDomain: getCSCEndpoint(),
                }
              ),
            }
          : domainInfo.cscContactID;
      }),
    });
  } else if (relatedContacts && relatedContacts.length) {
    display.push({
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_readonly_related_contacts',
        defaultMessage: 'Related contacts:',
      }),
      value: relatedContacts.map(contact => {
        const filtered = contactList?.find(
          element => element.contactID === contact
        );
        return filtered !== undefined && filtered.marketplaceDomainName !== null
          ? {
              text: contact,
              href: renderUrl(
                // eslint-disable-next-line max-len
                `/gp/stores/${filtered?.marketplaceDomainName}/gp/communications/issue-manager?initialCommId=${contact}`,
                {
                  externalDomain: getCSCEndpoint(),
                }
              ),
            }
          : contact;
      }),
    });
  }

  // Tenets
  if (tenets && tenets.length)
    display.push({
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_readonly_tenets',
        defaultMessage: 'Tenets:',
      }),
      value: tenets.map(tenet => {
        return getLabelFromValue(tenet, getAllTenets(intl));
      }),
    });

  // Behaviours
  if (behaviors && behaviors.length)
    display.push({
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_readonly_behaviours',
        defaultMessage: 'Behaviors:',
      }),
      value: behaviors?.map(behaviour => {
        return getLabelFromValue(behaviour, getAllBehaviors(intl));
      }),
    });

  // Metric
  if (relatedMetric !== null)
    display.push({
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_readonly_related_metric',
        defaultMessage: 'Related metric:',
      }),
      value: getLabelFromValue(relatedMetric, getAllMetrics()),
    });

  // Action
  display.push({
    label: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_label_readonly_assigned_action',
      defaultMessage: 'Assigned action:',
    }),
    value: assignedAction,
  });

  // Action status
  display.push({
    label: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_label_readonly_assigned_action_status',
      defaultMessage: 'Assigned action status:',
    }),
    value:
      (status === StatusOptionsObject.CLOSED &&
        intl.formatMessage({
          id: 'cs_perfportal_status_complete',
          defaultMessage: 'Complete',
        })) ||
      (status === StatusOptionsObject.PENDING &&
        intl.formatMessage({
          id: 'cs_perfportal_status_pending',
          defaultMessage: 'Pending',
        })) ||
      (status === StatusOptionsObject.DELIVERED &&
        intl.formatMessage({
          id: 'cs_perfportal_status_delivered',
          defaultMessage: 'Delivered',
        })) ||
      intl.formatMessage({
        id: 'cs_perfportal_status_open',
        defaultMessage: 'Open',
      }),
    callback: isTeam
      ? {
          text:
            status === StatusOptionsObject.OPEN
              ? intl.formatMessage({
                  id: 'cs_perfportal_status_action_mark_complete',
                  defaultMessage: 'Mark complete',
                })
              : '',
          action:
            status === StatusOptionsObject.OPEN && handleReplace
              ? (): void =>
                  handleReplace({
                    ...detail,
                    status: StatusOptionsObject.CLOSED,
                  })
              : // eslint-disable-next-line @typescript-eslint/no-empty-function
                (): void => {},
        }
      : undefined,
  });

  return display;
};

interface CoachingDetailsContainerProps {
  contactList: ContactFromGetter[];
  isEdit: boolean;
  userId: string;
}

export const Container: React.FC<CoachingDetailsContainerProps> = ({
  contactList,
  isEdit,
  userId,
}: CoachingDetailsContainerProps): React.ReactElement => {
  const { values, errors, touched }: FormikProps<CoachingFormInputData> =
    useFormikContext();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(
    isEdit ? values.coachingDetails.length - 1 : -1
  );
  const [initialValues, setInitialValues] = useState(
    editing > -1
      ? values.coachingDetails[editing]
      : defaultCoachingDetailsValues
  );
  const intl = useIntl();
  const maxTopicCount = 3;
  const inProgress: boolean = adding || editing > -1;
  const atLimit: boolean =
    values.coachingDetails.filter(
      detail => detail.status !== StatusOptionsObject.CLOSED
    ).length >= maxTopicCount;

  const messages = {
    inProgress: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_add_disabled_in_progress',
      defaultMessage:
        'Please complete or cancel the coaching detail in progress.',
      description: 'Add new disabled for work in progress tooltip',
    }),
    atLimit: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_add_disabled_max',
      defaultMessage:
        'You have already completed the maximum number of coaching topics.',
      description: 'Add new disabled for maximum coaching details tooltip',
    }),
    markedComplete: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_delete_disabled_complete',
      defaultMessage:
        'You cannot delete a coaching detail once marked complete.',
      description: 'Delete disabled for complete coaching detail',
    }),
  };

  return (
    <FieldArray name='coachingDetails'>
      {({ push, remove, replace }): ReactNode => {
        const handleCancel = (): void => {
          setInitialValues(defaultCoachingDetailsValues);
          setAdding(false);
          setEditing(-1);
        };

        const handlePush = (value: CoachingDetailsInput): void => {
          push(value);
          handleCancel();
        };

        const handleReplace = (
          value: CoachingDetailsInput,
          index: number = editing
        ): void => {
          replace(index, value);
          handleCancel();
        };

        return (
          <Column spacing='400'>
            {values.coachingDetails.map((coachingDetail, index) => (
              <div
                key={`detaildisplay_${index}_${coachingDetail.coachingTopic}`}
              >
                {editing !== index && (
                  <FieldArrayDisplay
                    mapKey={`detaildisplay_${index}`}
                    displayValues={convertDetailsToDisplay(
                      coachingDetail,
                      intl,
                      detail => handleReplace(detail, index),
                      true,
                      contactList
                    )}
                    handlers={
                      coachingDetail.status === StatusOptionsObject.PENDING
                        ? {
                            edit: (): void => {
                              setEditing(index);
                              setInitialValues(coachingDetail);
                            },
                            remove: (): void => remove(index),
                          }
                        : undefined
                    }
                    disableHandlers={{
                      edit: inProgress && messages.inProgress,
                      delete: false,
                    }}
                  />
                )}
              </div>
            ))}

            {inProgress && (
              <Box type='outline' spacingInset='400'>
                <Subform
                  onsubmit={editing > -1 ? handleReplace : handlePush}
                  onreset={handleCancel}
                  initialValues={initialValues || defaultCoachingDetailsValues}
                  userId={userId}
                />
              </Box>
            )}

            <ConditionalTooltip
              tooltip={
                (inProgress && messages.inProgress) ||
                (atLimit && messages.atLimit)
              }
            >
              <Column>
                <Button
                  data-csa-c-type='button'
                  data-csa-c-slot-id='coaching-details-add-new-button-slot'
                  data-csa-c-content-id='coaching-details-add-new-button'
                  type='tertiary'
                  disabled={inProgress || atLimit}
                  onClick={(): void => setAdding(true)}
                  data-cy='coaching-details-add-new'
                >
                  <FormattedMessage
                    id='cs_perfportal_add_new'
                    defaultMessage='Add new'
                    description='Button to add new'
                  />
                </Button>
              </Column>
            </ConditionalTooltip>

            {touched.coachingDetails &&
              typeof errors.coachingDetails === 'string' && (
                <ErrorMessage message={errors.coachingDetails} />
              )}
          </Column>
        );
      }}
    </FieldArray>
  );
};

export default Container;
